/**
 * The registry dump, as the browser sees it.
 *
 * WHY THE BROWSER IS FED RATHER THAN ASKING. Nothing in the web tier has Minecraft on its
 * classpath, and a 1.21.1 item id is meaningless without the modpack that defines it — so
 * "what items exist", "what is this one called" and "how many stack" are questions only the
 * running server can answer. It answers them once, at boot, into a static file
 * (`RegistryDumpWriter` in `:fabric`), and this module reads it.
 *
 * THE LOOKUP IS BY `x-enum-source`, NEVER BY FIELD. The generator stamps
 * `x-enum-source: minecraft:item` on an id field because of what the field IS; the dump keys
 * its sections by the same strings; this module is a map read between the two. That is what
 * makes a config written six months from now inherit autocomplete with no change here — and
 * it is why nothing in this file, or in any widget, contains the word "kit" or "warp".
 *
 * TWO SOURCES, ONE SHAPE — the same arrangement `schema/source.ts` uses and for the same
 * reason. In dev and in the Playwright harness the artifacts come off disk from
 * `web/deploy/fixtures/artifacts` via `vite-plugin-dev-artifacts`; in production they come
 * from the backend sidecar's `ARTIFACT_DIR`. One knob, `VITE_ARTIFACT_BASE`, and it genuinely
 * turns something.
 *
 * DEGRADING IS THE NORMAL CASE, NOT THE ERROR CASE. A server that has never booted, an
 * operator who has not copied the artifacts across, a registry the modpack does not have —
 * all of these end with "no suggestions for this field", and every widget must stay fully
 * usable typing a raw id. So nothing here throws at the consumer; a failed load is recorded
 * and reported once, and every lookup answers "nothing".
 */
import { sidecarDetail } from '../api/sidecarDetail';

/** One pickable value. `name` and `maxCount` are present only where they mean something. */
export interface RegistryEntry {
  readonly id: string;
  readonly name?: string;
  readonly maxCount?: number;
}

export interface RegistrySection {
  readonly count: number;
  readonly entries: readonly RegistryEntry[];
}

export interface RegistryDump {
  readonly version: number;
  readonly 'x-mod-version': string;
  readonly 'x-content-hash': string;
  readonly registries: Readonly<Record<string, RegistrySection>>;
}

/** `icons/index.json`: which ids have a real texture, and what to draw for the rest. */
export interface IconIndex {
  readonly version: number;
  readonly 'x-content-hash': string;
  readonly fallback: string;
  readonly count: number;
  readonly ids: readonly string[];
}

/**
 * The document shape this build understands. A server ahead of the deployed SPA is a real
 * possibility (the mod updates on its own schedule), and rendering half of a document whose
 * shape changed is worse than offering no suggestions.
 */
export const SUPPORTED_DUMP_VERSION = 1;

/** Served by `vite-plugin-dev-artifacts` from the committed fixture directory. */
export const FIXTURE_BASE = '/artifacts/';

/**
 * The sidecar's artifact routes: GET/HEAD, unauthenticated (an `<img src>` cannot carry a
 * bearer), content-hashed and conditional. A deployment that never filled `ARTIFACT_DIR` gets a
 * 503 naming it — the reason then carries the sidecar's own `detail` — and falls back to no
 * suggestions rather than icons. See `web/README.md`.
 */
export const BACKEND_BASE = '/v1/artifacts/';

export const DUMP_FILE = 'registry-dump.json';
export const ICON_INDEX_FILE = 'icons/index.json';

/**
 * Picks the base URL. Exported and tested rather than inlined — "the knob that turned
 * nothing" is a mistake this repository has already paid for once.
 */
export function resolveArtifactBase(env: Readonly<Record<string, string | boolean | undefined>>): string {
  const configured = env['VITE_ARTIFACT_BASE'];
  if (typeof configured === 'string' && configured !== '') {
    return configured.endsWith('/') ? configured : `${configured}/`;
  }
  return FIXTURE_BASE;
}

export function artifactSourceKind(base: string): 'fixture' | 'backend' {
  return base === FIXTURE_BASE ? 'fixture' : 'backend';
}

/**
 * `minecraft:diamond` → `minecraft/diamond.png`. The same derivation `IconDumpWriter`
 * uses when it lays the tree down, which is why the index is a list of ids rather than a map
 * of id to path: at ~1,500 entries, storing the path would be a third of the file to say
 * something both ends can compute.
 */
export function iconPathFor(id: string): string | null {
  const colon = id.indexOf(':');
  if (colon <= 0 || colon === id.length - 1) return null;
  return `icons/${id.slice(0, colon)}/${id.slice(colon + 1)}.png`;
}

/**
 * Everything the app knows about the modpack, or an honest account of why it knows nothing.
 *
 * One object rather than two loadables because every consumer wants both halves and the
 * failure modes are the same sentence; a widget should not have to spell out four states.
 */
export interface RegistryCatalog {
  readonly state: 'loading' | 'ready' | 'unavailable';
  /** Why there are no suggestions. Written for an admin, empty when `state` is `ready`. */
  readonly reason: string;
  readonly modVersion: string;
  readonly sources: readonly string[];
  /** Icon-bearing ids, and the hash their URLs are versioned with. */
  readonly iconHash: string;
  entriesFor(source: string | undefined): readonly RegistryEntry[];
  entry(source: string | undefined, id: string): RegistryEntry | undefined;
  /** An absolute URL for an item's icon, or `null` when it must render the fallback cube. */
  iconUrl(id: string): string | null;
}

/** The catalog every consumer sees before anything has loaded, and if nothing ever does. */
export function emptyCatalog(state: 'loading' | 'unavailable', reason = ''): RegistryCatalog {
  return {
    state,
    reason,
    modVersion: '',
    sources: [],
    iconHash: '',
    entriesFor: () => [],
    entry: () => undefined,
    iconUrl: () => null,
  };
}

export interface LoadOptions {
  readonly base?: string;
  readonly fetchImpl?: typeof fetch;
  readonly signal?: AbortSignal;
}

/**
 * Fetches the dump and the icon index and folds them into a catalog.
 *
 * The icon index is OPTIONAL in a way the dump is not: without the dump there are no
 * suggestions at all, but without the index every item simply renders the neutral cube,
 * which is a defined and acceptable state (`web/README.md` -> "Icons and the
 * vanilla-texture question"). So a failed icon fetch is
 * swallowed and a failed dump fetch is reported.
 */
export async function loadCatalog(options: LoadOptions = {}): Promise<RegistryCatalog> {
  const base = options.base ?? FIXTURE_BASE;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const kind = artifactSourceKind(base);

  let dump: RegistryDump;
  try {
    // Both documents are fetched BARE, and must be. They are what this app reads in order to
    // learn a version, so it cannot name their version before it has them - and replaying the
    // hash it saw last session would be a cache trap, not a cache: the browser would answer
    // `?v=<old>` out of its own immutable store and we would never notice the operator added
    // a mod. The sidecar revalidates them instead, so a repeat load is a 304 rather than
    // 276 KB. Only the icon URLs are content-addressed, because only they have an out-of-band
    // referrer - the index fetched just below.
    const response = await fetchImpl(`${base}${DUMP_FILE}`, {
      headers: { accept: 'application/json' },
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
    if (!response.ok) {
      const said = await sidecarDetail(response);
      return emptyCatalog('unavailable', unavailableBecause(kind, `${base}${DUMP_FILE} answered ${response.status}`, said));
    }
    dump = (await response.json()) as RegistryDump;
  } catch (cause) {
    if (options.signal?.aborted === true) return emptyCatalog('loading');
    return emptyCatalog('unavailable', unavailableBecause(kind, `${base}${DUMP_FILE} could not be reached`));
  }

  if (dump === null || typeof dump !== 'object' || typeof dump.registries !== 'object' || dump.registries === null) {
    return emptyCatalog('unavailable', `${base}${DUMP_FILE} is not a registry dump.`);
  }
  if (dump.version !== SUPPORTED_DUMP_VERSION) {
    // A server ahead of the deployed SPA is a real possibility - the mod updates on its own
    // schedule - and rendering half of a document whose shape changed is worse than offering
    // nothing. The reason says which two builds disagree; the reassurance that a field still
    // works is added ONCE, by `RegistryStatus`, so it is not repeated here.
    return emptyCatalog(
      'unavailable',
      `The server wrote a version ${String(dump.version)} registry dump and this editor reads version ${SUPPORTED_DUMP_VERSION}, ` +
        'so the two are not on the same build.',
    );
  }

  const icons = await loadIconIndex(base, fetchImpl, options.signal);
  return catalogOf(base, dump, icons);
}

async function loadIconIndex(base: string, fetchImpl: typeof fetch, signal?: AbortSignal): Promise<IconIndex | null> {
  try {
    const response = await fetchImpl(`${base}${ICON_INDEX_FILE}`, {
      headers: { accept: 'application/json' },
      ...(signal === undefined ? {} : { signal }),
    });
    if (!response.ok) return null;
    const index = (await response.json()) as IconIndex;
    return Array.isArray(index?.ids) ? index : null;
  } catch {
    return null;
  }
}

/** Builds the lookup structures once, so a form with 200 fields is 200 map reads. */
export function catalogOf(base: string, dump: RegistryDump, icons: IconIndex | null): RegistryCatalog {
  const bySource = new Map<string, readonly RegistryEntry[]>();
  const indexed = new Map<string, Map<string, RegistryEntry>>();
  for (const [source, section] of Object.entries(dump.registries)) {
    const entries = Array.isArray(section?.entries) ? section.entries : [];
    bySource.set(source, entries);
    indexed.set(source, new Map(entries.map((entry) => [entry.id, entry])));
  }

  const iconIds = new Set(icons?.ids ?? []);
  const iconHash = icons?.['x-content-hash'] ?? '';

  return {
    state: 'ready',
    reason: '',
    modVersion: typeof dump['x-mod-version'] === 'string' ? dump['x-mod-version'] : '',
    sources: [...bySource.keys()].sort(),
    iconHash,
    entriesFor: (source) => (source === undefined ? [] : (bySource.get(source) ?? [])),
    entry: (source, id) => (source === undefined ? undefined : indexed.get(source)?.get(id)),
    iconUrl: (id) => {
      if (!iconIds.has(id)) return null;
      const path = iconPathFor(id);
      if (path === null) return null;
      // `iconHash` versions the TREE, not this file, and that is deliberate on both sides:
      // it is the one token this app is in a position to name (it came out of the index we
      // just fetched), and `backend/src/artifacts.ts` answers `immutable` for exactly it on
      // any `icons/**.png`. Sound because `IconDumpWriter.dump` rewrites the whole tree
      // whenever the hash changes, so this URL can never mean a second image. Change the
      // token here and the backend stops honouring it - `artifacts.contract.test.ts` fails.
      return iconHash === '' ? `${base}${path}` : `${base}${path}?v=${encodeURIComponent(iconHash)}`;
    },
  };
}

/** `said` is the sidecar's own sentence for the refusal, when it wrote one: it is more exact than ours. */
function unavailableBecause(kind: 'fixture' | 'backend', detail: string, said: string | null = null): string {
  if (said !== null) return `${detail}: ${said}`;
  return kind === 'fixture'
    ? `${detail}. In development the registry dump is served from web/deploy/fixtures/artifacts by vite-plugin-dev-artifacts; run the app through Vite rather than a static file server.`
    : `${detail}. The registry dump is written by the game server at boot into config/cobblemondimensions/web and served by the sidecar; check that it has been copied into ARTIFACT_DIR.`;
}
